// src/components/richText/RichText.jsx
//
// Markdown for lesson BODY TEXT (authoring-5b-markdown): **bold**, *italic*,
// ==highlight==, lists, quotes. One component, used by the technique / analysis /
// vocabulary / guide renderers, the worked examples and the admin preview, so a body
// looks the same everywhere it appears.
//
// Reuses the stack the repo already ships (react-markdown + remark-gfm; see
// ChatMarkdown.jsx). No new dependency, no rehype-raw, no raw-HTML path, no
// dangerouslySetInnerHTML. What each plugin is for -- and the live content that made it
// necessary -- is documented in richTextPipeline.js.
//
// TWO CONTRACTS THIS COMPONENT KEEPS:
//   1. A body with NO markdown in it renders exactly as it did as a single
//      <p class="whitespace-pre-wrap">. Typography comes from `className` -- pass the
//      classes the old <p> carried -- and spacing from richText.css + remarkPreserveSpacing.
//   2. It renders a <div> of blocks. It must never be placed inside a <p> or a <span>
//      (a list inside a paragraph is invalid HTML and browsers re-parent it).
//
// NOT for headings, labels, titles, quiz text or AI output. `blocks.unit` in particular
// reads "2. Timed Planning ..." on 60 live sections and would become a numbered list.
//
// Glossary terms: composes with AnnotationsContext exactly as <AnnotatedText> did --
// first occurrence of each term per body -- but over markdown text nodes (remarkGlossary).
import React, { useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import GlossTerm from '../InlineGlossPopover.jsx';
import AnnotatedText, { AnnotationsContext, TermPopover } from '../AnnotatedText.jsx';
import { preEscape, remarkPluginsFor, REHYPE_PLUGINS, SAFE_LINK, isTooDeep } from './richTextPipeline.js';
import './richText.css';

// Heading levels are CLAMPED: `#` in a body is a small display heading inside a card,
// never an <h1>. They are real headings for assistive tech, at levels below the lesson
// title (h1) and the section headings, so the document outline stays sane.
const heading = (level, cls) => function RichHeading({ children, style }) {
    const Tag = 'h' + level;
    return <Tag className={'font-display btl-rich-h ' + cls} style={style}>{children}</Tag>;
};

function buildComponents(byLower) {
    return {
        p: ({ children, style }) => <p style={style}>{children}</p>,
        h1: heading(4, 'btl-rich-h1'),
        h2: heading(5, 'btl-rich-h2'),
        h3: heading(6, 'btl-rich-h3'),
        h4: heading(6, 'btl-rich-h3'),
        h5: heading(6, 'btl-rich-h3'),
        h6: heading(6, 'btl-rich-h3'),
        ul: ({ children, style }) => <ul style={style}>{children}</ul>,
        // `start` MUST be forwarded: 9 of the 16 numbered lists in live content begin at
        // 2, 3, 4, 6 or 7 (exam question numbers). Dropping it shows "Question 6" as "1.".
        ol: ({ children, style, start }) => <ol style={style} start={start}>{children}</ol>,
        // `value` = the numeral the author typed (remarkFaithfulNumbers).
        li: ({ children, style, value }) => <li style={style} value={value}>{children}</li>,
        blockquote: ({ children, style }) => <blockquote style={style}>{children}</blockquote>,
        hr: ({ style }) => <hr style={style} />,
        a: ({ href, children }) => (
            typeof href === 'string' && SAFE_LINK.test(href)
                ? <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                : <span>{children}</span>
        ),
        // Images are switched off in the tokenizer (labelStartImage). This is the second
        // lock: if one ever reached here it would render nothing rather than load a URL.
        img: () => null,
        table: ({ children, style }) => <div className="btl-rich-table" style={style}><table>{children}</table></div>,
        'gloss-term': ({ children, ...rest }) => {
            const term = byLower ? byLower.get(String(rest['data-term'] || '')) : null;
            if (!term) return <>{children}</>;
            return <GlossTerm label={'Definition: ' + term.name} popover={<TermPopover term={term} />}>{children}</GlossTerm>;
        },
    };
}

// THE FALLBACK: the exact markup every body had before 5b. Used when the text is nested
// deeper than a person would type (isTooDeep) or if the markdown renderer ever throws --
// a lesson degrades to plain text, it never goes blank.
function PlainBody({ text, className, annotate }) {
    return <p className={className + ' whitespace-pre-wrap'}>{annotate ? <AnnotatedText>{text}</AnnotatedText> : text}</p>;
}
class RichTextBoundary extends React.Component {
    constructor(props) { super(props); this.state = { failed: false }; }
    static getDerivedStateFromError() { return { failed: true }; }
    componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false }); }
    render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

// `annotate={false}` switches glossary terms off for a field that never had them (the
// vocabulary example sentence was plain text before 5b, and stays free of popovers).
function RichText({ text, className = '', annotate = true }) {
    const contextMatcher = useContext(AnnotationsContext);
    const matcher = annotate ? contextMatcher : null;
    const remarkPlugins = useMemo(() => remarkPluginsFor(remarkGfm, matcher), [matcher]);
    const components = useMemo(() => buildComponents(matcher ? matcher.byLower : null), [matcher]);
    const source = typeof text === 'string' ? text : '';
    const prepared = useMemo(() => preEscape(source), [source]);
    if (!source.trim()) return null;
    const plain = <PlainBody text={source} className={className} annotate={annotate} />;
    if (isTooDeep(source)) return plain;
    return (
        <RichTextBoundary fallback={plain} resetKey={source}>
            <div className={'btl-rich ' + className}>
                <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={REHYPE_PLUGINS} components={components}>
                    {prepared}
                </ReactMarkdown>
            </div>
        </RichTextBoundary>
    );
}

// Memoised: react-markdown re-parses on every render, and a lesson re-renders on each
// Read-more / reveal / popover toggle. The props are primitives, so this is exact.
export default React.memo(RichText);
